import { SearchBoxWidgetProps } from '@/types/props/components';
import { useRouter } from 'next/router';
import handleKeyDown from '@/utilites/search/handle-key-down';
import { useTranslation } from 'next-i18next';
import translationFallback from '@/utilites/translations/translationFallback';
import SpiritoryInput from '@/components/common/Forms/SpiritoryInput';
import { IoCloseOutline } from 'react-icons/io5';
import { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { useSearchBox, useInstantSearch, useHits } from 'react-instantsearch';
import type { SkuDTO } from '@/types/api/sku';
import useEvents from '@/hooks/useEvents';

export default function SearchBoxWidget({
  setShowResult,
  addButton,
  buttonProps,
  searchContainerClassName,
  inputClassName,
  saveSearchQuery,
  userId,
}: SearchBoxWidgetProps) {
  const { refine } = useSearchBox();
  const { results } = useInstantSearch();
  const router = useRouter();
  const { t } = useTranslation('common');
  const { items } = useHits<SkuDTO>();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { fireSearchBarFilterFilledEvent } = useEvents(query, items);
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Only fire if a filter is active (for now, always fire on Enter)
      fireSearchBarFilterFilledEvent();
    }
    return handleKeyDown(e, router.push, setShowResult);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);

    if (saveSearchQuery) saveSearchQuery(newQuery);

    setShowResult(newQuery.length > 2);
    refine(newQuery);
  };

  const clearSearch = () => {
    setQuery('');
    setShowResult(false);

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  return (
    <div
      className={clsx(
        'tw-flex tw-h-12 tw-w-full tw-items-stretch tw-gap-2',
        searchContainerClassName
      )}
    >
      <div className="tw-relative tw-flex-grow">
        <SpiritoryInput
          ref={inputRef}
          value={query}
          onChange={onChange}
          onKeyDown={onKeyDown}
          searchIcon
          className={clsx(inputClassName)}
          placeholder={`${translationFallback(
            'search_something',
            t('search_something'),
            'Search something here'
          )}...`}
          rightIcon={
            query ? (
              <IoCloseOutline className="tw-cursor-pointer" onClick={clearSearch} />
            ) : undefined
          }
          onBlur={() => {}}
        />
      </div>
      {addButton && <button {...buttonProps} />}
    </div>
  );
}
